import type {
  LaunchStatusHy,
  PriceSettingsDto,
  PriceStatus,
  SimulationStatus,
  UpdateSimulationAssignmentRequestBody,
} from '@/api';
import type { LockableField } from '@/components/table/table-types';
import type {
  MRT_ColumnFiltersState,
  MRT_TableInstance,
  MRT_Updater,
} from 'material-react-table';
import type { RefObject } from 'react';
import type { SimulationView } from '../simulation-view-constants';

export type RowData = {
  productId: number;
  productName: string;
  productCount: number;
  license: string;
  themeName: string;
  groupName: string;
  productClassLevelOneName: string;
  productGroupName: string;
  productClassLevelTwoName: string;
  productCategoryName: string;
  developmentOwnershipName: string;
  productCompositionName: string;
  superSegmentName: string;
  themeCategoryName: string;
  isRareItem: boolean;
  boxSizeName: string;
  businessToBusinessLaunchDate: Date | null;
  localLaunchDate: Date | null;
  localExitDate: Date | null;
  directToConsumerLifelong: boolean | null;
  sizeDimension: string;
  pieceCount: number | null;
  smartPlayName: string;
  playTypeName: string;
  topThemeName: string;
  productAudience: string;
  launchStatus: LaunchStatusHy;
  priceUnitId: number;
  primaryKey: string;
  localLeadRrp: number;
  localLeadRm: number;
  localLeadLp: number;
  asIsRrp: number;
  asIsRm: number;
  asIsLp: number;
  fmcLocal: number;
  forecastedDemand: number;
  priceStatus: PriceStatus;
  ppcMark: string;
  rrp: number;
  rm: number;
  lp: number;
  hasPriceChange: boolean;
  hasFutureLpChange: boolean;
  nearestFutureModifiedLp: number | null;
  nearestFutureModifiedLpValidFrom: Date | null;
  rrpIndexLocalLead: number;
  listPriceFmcFactor: number;
  lpIndexLocalLead: number;
  lpDelta: number;
  nip: number;
  llNip: number;
  llNipIndex: number;
  comment: string;
  vat: number;
  useRrp: boolean;
  currency: string;
  currencyDecimals: number;
  localLeadCurrencyDecimals: number;
  globalLeadCurrencyDecimals: number;
  globalLeadIer: number;
  localLeadVat: number;
  globalLeadLp: number;
  isExemptFromFiltering: boolean;
  localLeadFunctionalDiscount: number;
  localLeadSalesDiscount: number;
  globalLeadFunctionalDiscount: number;
  globalLeadSalesDiscount: number;
  valueBasedPrice: number;
  tradeContributionDiscount: number;
  localLeadTradeContributionDiscount: number;
};

export type RowDataForDisplay = Omit<
  RowData,
  | 'license'
  | 'productClassLevelOneName'
  | 'isRareItem'
  | 'businessToBusinessLaunchDate'
  | 'localLaunchDate'
  | 'localExitDate'
  | 'directToConsumerLifelong'
  | 'pieceCount'
  | 'launchStatus'
  | 'rm'
  | 'rrp'
  | 'lp'
  | 'localLeadRrp'
  | 'localLeadRm'
  | 'asIsRm'
  | 'asIsRrp'
  | 'asIsLp'
  | 'localLeadLp'
  | 'hasFutureLpChange'
  | 'listPriceFmcFactor'
  | 'fmcLocal'
  | 'hasPriceChange'
  | 'lpDelta'
  | 'nip'
  | 'llNip'
  | 'forecastedDemand'
  | 'vat'
  | 'nearestFutureModifiedLp'
  | 'nearestFutureModifiedLpValidFrom'
> & {
  license: string;
  productClassLevelOneName: string;
  isRareItem: string;
  businessToBusinessLaunchDate: string;
  localLaunchDate: string;
  localExitDate: string;
  directToConsumerLifelong: string;
  pieceCount: string;
  launchStatus: string;
  localLeadRrp: string;
  localLeadRm: string;
  asIsRm: string;
  asIsRrp: string;
  asIsLp: string;
  rm: string;
  rrp: string;
  lp: string;
  localLeadLp: string;
  hasFutureLpChange: string;
  listPriceFmcFactor: string;
  fmcLocal: string;
  hasPriceChange: string;
  lpDelta: string;
  nip: string;
  llNip: string;
  forecastedDemand: string;
  vat: string;
};

export type TableColumn = Exclude<
  keyof RowData,
  | 'useRrp'
  | 'currency'
  | 'globalLeadIer'
  | 'localLeadVat'
  | 'globalLeadLp'
  | 'dominantMaterialId'
  | 'isExemptFromFiltering'
  | 'nearestFutureModifiedLp'
  | 'nearestFutureModifiedLpValidFrom'
  | 'localLeadFunctionalDiscount'
  | 'localLeadSalesDiscount'
  | 'globalLeadFunctionalDiscount'
  | 'globalLeadSalesDiscount'
  | 'tradeContributionDiscount'
  | 'localLeadTradeContributionDiscount'
>;
export type PricelistSettings = PriceSettingsDto & {
  averageVat: number;
};

export type ColumnCategory =
  | 'priceList'
  | 'productData'
  | 'localLead'
  | 'localAsIs'
  | 'pricingSimulation'
  | 'simulationResult'
  | 'currentYear'
  | 'other';

export enum SIM_MODAL {
  Download = 'download',
  Publish = 'publish',
  Upload = 'upload',
  PriceSettings = 'price-settings',
  SendTo = 'send-to',
  AssignTo = 'assign-to',
  AddRemoveWatchers = 'add-remove-watchers',
}

export type PriceListData = {
  localRrp: number;
  localLp: number;
  localNip: number;
  altRrpIndex: number;
  altLpIndex: number;
  altNipIndex: number;
};

export type HarmonizationPivotRow = {
  themeName: string;
  productId: number;
  productName: string;
  launchStatus: string;
  priceListData: Record<number, PriceListData>;
};

export type PriceListDataForDisplay = Omit<
  PriceListData,
  'localRrp' | 'localLp' | 'localNip'
> & {
  localRrp: string;
  localLp: string;
  localNip: string;
};

export type HarmonizationPivotRowForDisplay = Omit<
  HarmonizationPivotRow,
  'priceListData'
> & {
  priceListData: Record<number, PriceListDataForDisplay>;
};

export type ReviewerAssignmentRequest = {
  assignment: UpdateSimulationAssignmentRequestBody;
  afterAssign: 'close' | 'add-watchers';
};

export const CurrentVersionStatus = 'Current' as const;
export type CurrentVersionStatus = typeof CurrentVersionStatus;
export type VersionStatus = SimulationStatus | CurrentVersionStatus;

export type SimulationTableToolbarProps = {
  table?: MRT_TableInstance<RowDataForDisplay>;
  canUploadFile?: boolean;
  onDownloadModalOpen?: () => void;
  lockedColumn: LockableField | undefined;
  isLel: boolean;
  selectedView: SimulationView;
  isPublishDisabled: boolean;
  simulationId: number;
  downloadTriggerRef: RefObject<HTMLLIElement | null>;
  publishTriggerRef: RefObject<HTMLButtonElement | null>;
  setLockedColumn: (column: LockableField | undefined) => void;
  setPreference: (preference: 'lel' | 'simple') => void;
  setSelectedView: (view: SimulationView) => void;
  setShowModal: (modal: SIM_MODAL | null) => void;
  columnFilters?: MRT_ColumnFiltersState;
  setColumnFilters?: (updater: MRT_Updater<MRT_ColumnFiltersState>) => void;
};
